import { useMemo, useRef, useState } from "react";
import { CheckIcon, ShieldIcon, ZapIcon } from "@/components/icons";
import type { Offer, OfferPath, PriceOption } from "@/services/rto";
import { money } from "@/utils/format";

/* One card per (path, pricing option) — flattened FROM the backend offer.
   The backend already applies the matrix (FR-3.2: tier riders choose,
   linear stays linear, unlimited stays unlimited, CPX keep = linear only),
   so nothing here re-derives eligibility rules, prices or collateral. */

export interface PlanCard {
  path: OfferPath;
  option: PriceOption;
  recommended: boolean;
}

export const flattenOffer = (offer: Offer): PlanCard[] => {
  const cards: PlanCard[] = [];
  for (const path of offer.paths) {
    for (const option of path.options) {
      cards.push({ path, option, recommended: false });
    }
  }
  if (cards.length > 0) cards[0].recommended = true;
  return cards;
};

const FAQ: [string, string][] = [
  [
    "Se eu quiser devolver a moto antes do fim do contrato, tem multa?",
    "Sim. Valem as mesmas regras de cancelamento do seu contrato de aluguel atual.",
  ],
  [
    "Precisa de análise de crédito pra migrar?",
    "Não. A Vammo confia em você: sem consulta de score, sem comprovante de renda, sem burocracia.",
  ],
  [
    "O que acontece com a caução que já paguei?",
    "É descontada do valor novo. Você paga só a diferença, à vista ou parcelada na semanal.",
  ],
  [
    "Posso comprar a moto antes dos 36 meses?",
    "Não. A compra só pode ser feita no fim do contrato.",
  ],
  [
    "Preciso pagar algum valor adicional ao fim dos 36 meses?",
    "Se você não tiver nenhuma pendência com a Vammo, o valor pago da sua caução é usado na quitação da sua moto e não é necessário nenhum valor adicional. Caso você tenha atrasos ou multas a serem debitados da caução, você paga apenas a diferença.",
  ],
  [
    "Como ficam manutenção e troca de bateria?",
    "Iguais a hoje: incluídas no plano, nos mesmos postos, sem custo extra — só o valor variável por km.",
  ],
  [
    "Quando e como a moto passa a ser minha?",
    "Depois dos 36 meses, com tudo em dia, a Vammo faz a transferência no Detran em até 10 dias úteis após você entregar a documentação. Os custos de transferência ficam por sua conta.",
  ],
  [
    "E se a moto for roubada ou eu sofrer um acidente grave?",
    "Mesmo processo do aluguel: boletim de ocorrência, moto reserva, coparticipação de R$1.500 e análise do caso.",
  ],
  [
    "E se eu atrasar um pagamento?",
    "Valem as mesmas regras de atraso, juros e bloqueio (IoT) do contrato de aluguel atual.",
  ],
  [
    "No final, a bateria e o carregador vêm inclusos?",
    "Não automaticamente. No fim dos 36 meses você escolhe: seguir na rede de troca da Vammo, pagando só a energia, ou comprar bateria e carregador pra ficar com tudo 100% seu.",
  ],
  [
    "Tenho moto reserva durante o contrato?",
    "Sim. Durante os 36 meses o direito é o mesmo de hoje. Depois que a moto é sua, o benefício encerra.",
  ],
];

function familyLabel(offer: Offer, card: PlanCard): string {
  if (card.path.path === "CHANGE") return "Comfort 0km";
  const fam = offer.segment.family === "CPX" ? "CPX" : "Comfort";
  return offer.segment.bike_condition === "NEW" ? `${fam} 0km` : `${fam} usada`;
}

function PlanCardView({
  offer,
  card,
  onPick,
}: {
  offer: Offer;
  card: PlanCard;
  onPick: (card: PlanCard) => void;
}) {
  const keep = card.path.path === "KEEP";
  const unlimited = card.option.pricing_model === "UNLIMITED";
  const quote = card.path.collateral;
  const cls =
    "lpc-plan" + (card.recommended ? " lpc-plan--reco" : keep ? "" : " lpc-plan--change");
  const title = keep ? `Ficar com a sua ${familyLabel(offer, card)}` : "Trocar por uma Comfort 0km";
  const desc = keep
    ? unlimited
      ? "Continue com a moto que já é sua e migre pro Conquista no plano de km ilimitados."
      : "Continue com a moto que já é sua e migre pro Conquista no plano por km."
    : unlimited
      ? "Recomece numa Comfort 0km no plano ilimitado."
      : "Recomece numa Comfort 0km no plano por km.";
  const fine = keep
    ? card.path.tenure_credit_months > 0
      ? `Seu tempo de casa já conta: o contrato fica em ${card.path.effective_term_months} meses.`
      : "Cada semanal te deixa mais perto da moto no seu nome."
    : "Sem crédito de tempo de casa — os 36 meses começam do zero.";
  const priceJump = card.path.discloses_price_jump;

  return (
    <div className={cls} data-testid={`plan-${card.path.path}-${card.option.pricing_model}`}>
      {card.recommended ? (
        <span className="lpc-plan__badge">
          <CheckIcon />
          Recomendado
        </span>
      ) : null}
      <h3 className="lpc-plan__title">{title}</h3>
      <p className="lpc-plan__desc">{desc}</p>
      <div className="lpc-plan__sec">
        <div className="lpc-plan__eyebrow">
          <ZapIcon />
          Sua fatura passa a ser
        </div>
        <div className="lpc-plan__price">
          {money(card.option.weekly)} <small>/ semana</small>
        </div>
        <div className="lpc-plan__km">
          {unlimited
            ? "Km ilimitados"
            : `+ ${money(card.option.variable_rate ?? 0)} por km · teto ${money(card.option.ceiling ?? 0)}`}
        </div>
        <p className="lpc-plan__fine">{fine}</p>
        {priceJump ? (
          <p className="lpc-plan__fine">
            Atenção: a semanal muda em relação ao seu plano CPX atual.
          </p>
        ) : null}
      </div>
      <div className="lpc-plan__sec">
        <div className="lpc-plan__eyebrow">
          <ShieldIcon />
          Caução
        </div>
        <div className="lpc-plan__cauval">{money(quote.upfront.target)} à vista</div>
        <div className="lpc-plan__causub">
          ou {money(quote.installments.entry)} + {quote.installments.weekly_installments}×{" "}
          {money(quote.installments.installment_value)} na semanal
        </div>
        <p className="lpc-plan__fine">
          {keep
            ? `Você já pagou ${money(quote.paid)} — só a diferença é cobrada.`
            : quote.paid > 0
              ? `Você já pagou ${money(quote.paid)} — só a diferença é cobrada.`
              : "Valor integral da caução da moto nova."}
        </p>
      </div>
      <button className="lpc-plan__cta" onClick={() => onPick(card)}>
        {keep ? "Quero migrar e conquistar" : "Quero começar do zero"}
      </button>
    </div>
  );
}

export function PlansScreen({
  offer,
  onPick,
  onOptOut,
}: {
  offer: Offer;
  onPick: (card: PlanCard) => void;
  onOptOut: () => void;
}) {
  const cards = useMemo(() => flattenOffer(offer), [offer]);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [dot, setDot] = useState(0);
  const keepPath = offer.paths.find((p) => p.path === "KEEP");
  const showTenure = offer.tenure_months > 12 && keepPath;

  return (
    <section className="screen-enter" data-testid="screen-plans">
      <h2
        style={{
          textAlign: "center",
          fontSize: 22,
          textTransform: "uppercase",
          letterSpacing: "0.01em",
          lineHeight: 1.25,
          marginBottom: 24,
        }}
      >
        Opções de planos para conquistar a sua Vammo
      </h2>

      {showTenure ? (
        <div className="lpc-tenure" data-testid="tenure-block">
          <p className="lpc-tenure__lead">
            Você já está há <strong>{offer.tenure_months} meses</strong> na Vammo.
          </p>
          <p className="lpc-tenure__label">A moto já pode ser sua em</p>
          <div className="lpc-tenure__big" data-testid="tenure-months">
            {keepPath!.effective_term_months} meses
          </div>
          <p className="lpc-tenure__note">
            Metade do seu tempo de casa é descontado direto do prazo de você ficar com a sua moto
            atual.
            <br />
            <br />
            Quanto mais tempo com a gente, mais perto você já está.
          </p>
        </div>
      ) : null}

      <div
        className="lpc-carousel"
        ref={wrapRef}
        onScroll={() => {
          const wrap = wrapRef.current;
          if (!wrap) return;
          const cw = wrap.scrollWidth / (cards.length || 1);
          setDot(Math.max(0, Math.min(cards.length - 1, Math.round(wrap.scrollLeft / cw))));
        }}
      >
        {cards.map((card) => (
          <PlanCardView
            key={`${card.path.path}-${card.option.pricing_model}`}
            offer={offer}
            card={card}
            onPick={onPick}
          />
        ))}
      </div>
      <div className="lpc-dots">
        {cards.map((_, i) => (
          <span key={i} className={i === dot ? "is-active" : ""} />
        ))}
      </div>

      <button className="lpc-optout" data-testid="opt-out" onClick={onOptOut}>
        Quero manter meu plano atual
      </button>
      <p className="lpc-micro" style={{ textAlign: "center" }}>
        Prefere seguir alugando como sempre? Seu plano continua igual — nada muda.
      </p>

      <div className="lpc-faq">
        <h3>Perguntas frequentes</h3>
        {FAQ.map(([q, a]) => (
          <details key={q}>
            <summary>{q}</summary>
            <p>{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
